import React from 'react';
import { Lead, Category } from '../../types';
import { useI18n } from '../../context/I18nContext';
import { useTranslations } from '../../hooks/useTranslations';
import { useLeadOptions } from '../../utils/leadOptions';
import { useTableSelection } from '../../hooks/useTableSelection';
import Table, {
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableSelectionHeader,
  TableSelectionCell,
  TableActionsHeader,
  TableActionsCell,
  TableEmptyRow,
  tableRowSelectedClass,
} from '../ui/Table';
import ActionIconButton from '../ui/ActionIconButton';
import ContextMenu, { ContextMenuTarget } from '../ui/ContextMenu';
import { Edit, Trash2, ArrowDownLeft, ArrowUpRight, Users } from 'lucide-react';
import ContactIconWithCategory from '../ui/ContactIconWithCategory';
import Tooltip from '../ui/Tooltip';
import TruncateTooltip from '../ui/TruncateTooltip';
import InfiniteLoadMore from '../ui/InfiniteLoadMore';
import EmptyState from '../ui/EmptyState';
import LeadStatusBadge from './LeadStatusBadge';
import { neutralColors, highlightRing } from '../../styles/colors';
import { getLeadActionMenuItems, type LeadMenuActions } from '../../utils/leadActions';
import {
  buildContactOpenUrl,
  contactOpenLabelKey,
  resolveContactChannel,
} from '../../utils/contactOutbound';

// Ensures sector labels wrap reliably by removing non‑breaking spaces
// and allowing a break opportunity after '&'.
const normalizeWrap = (input?: string): string => {
  const text = input || '';
  return text
    .replace(/[\u00A0\u202F]/g, ' ')
    .replace(/&/g, '&\u200B');
};

interface LeadTableProps {
  leads: Lead[];
  categories: Category[];
  onEdit: (lead: Lead) => void;
  onDelete: (id: string) => void;
  getLeadProject?: (leadId: string) => any;
  leadMenuActions?: LeadMenuActions;
  projects?: any[];
  onBulkDelete?: (ids: string[]) => void;
  onRegisterToggleAll?: (toggleAll: () => void) => void;
  onRegisterBulkDelete?: (bulkDelete: () => void) => void;
  onRegisterClearSelection?: (clearSelection: () => void) => void;
  onSelectedCountChange?: (count: number) => void;
  onIsAllSelectedChange?: (isAll: boolean) => void;
  onSelectedLeadsChange?: (leads: Lead[]) => void;
  hideHeaderCheckbox?: boolean;
  hideRowCheckboxes?: boolean;
  hideBulkActionsBar?: boolean;
  // Infinite loading (Notion-like)
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  /** When true, empty list is due to filters — no create CTA */
  isFilteredEmpty?: boolean;
  onCreate?: () => void;
  /** Open the ⋯ menu on this lead (guided create-project flow). */
  guidedMenuLeadId?: string | null;
  onGuidedMenuClose?: () => void;
}

const LEAD_TABLE_COLUMN_COUNT = 9;

const LeadTable: React.FC<LeadTableProps> = ({
  leads,
  categories,
  onEdit,
  onDelete,
  getLeadProject,
  leadMenuActions,
  projects = [],
  onBulkDelete,
  onRegisterToggleAll,
  onRegisterBulkDelete,
  onRegisterClearSelection,
  onSelectedCountChange,
  onIsAllSelectedChange,
  onSelectedLeadsChange,
  hideHeaderCheckbox = false,
  hideRowCheckboxes = false,
  hideBulkActionsBar = false,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  isFilteredEmpty = false,
  onCreate,
  guidedMenuLeadId = null,
  onGuidedMenuClose,
}) => {
  const { t } = useI18n();
  const { formatDateShort } = useTranslations();
  const { getSectorLabel } = useLeadOptions();
  
  const {
    isAllSelected,
    selectedCount,
    handleSelectAll,
    handleSelectItem,
    isSelected,
  } = useTableSelection<Lead>({
    items: leads,
    onBulkDelete,
    onRegisterToggleAll,
    onRegisterBulkDelete,
    onRegisterClearSelection,
    onSelectedCountChange,
    onIsAllSelectedChange,
    onSelectedItemsChange: onSelectedLeadsChange,
  });

  const isFollowUpOverdue = (lead: Lead) => {
    return lead.nextActionAt && new Date(lead.nextActionAt) < new Date();
  };

  const emptyState = isFilteredEmpty ? (
    <EmptyState
      icon={Users}
      title={t('leads.noLeadsFound')}
      description={t('common.tryAdjustingFilters')}
    />
  ) : (
    <EmptyState
      icon={Users}
      title={t('leads.noLeads')}
      description={t('leads.noLeadsDescription')}
      actionLabel={onCreate ? t('leads.createFirst') : undefined}
      onAction={onCreate}
    />
  );

  return (
    <div className="space-y-4">
      <div className="border border-gray-200/60 rounded-xl shadow-sm bg-white overflow-hidden">
        <Table className="border-0 rounded-none" dataTableColumnCount={LEAD_TABLE_COLUMN_COUNT}>
          <TableHeader>
            <TableRow>
              <TableSelectionHeader
                isAllSelected={isAllSelected}
                selectedCount={selectedCount}
                onSelectAll={handleSelectAll}
                selectAllAriaLabel={t('projects.selectAll')}
                hidden={hideHeaderCheckbox || leads.length === 0}
              />
              <TableHead>{t('leads.singular')}</TableHead>
              {/* Slightly wider on sm/md tablet — table-fixed otherwise shares evenly with Status/Dir */}
              <TableHead className="hidden sm:table-cell text-left w-[32%] lg:w-[24%] xl:w-[20%]">
                {t('leads.contactValue')}
              </TableHead>
              <TableHead className="hidden sm:table-cell text-center p-0 w-10">
                <span className="sr-only">Dir</span>
              </TableHead>
              <TableHead className="hidden lg:table-cell text-center">{t('leads.sector')}</TableHead>
              <TableHead className="text-center w-[9rem]">{t('leads.status')}</TableHead>
              <TableHead className="hidden lg:table-cell text-center p-0 min-[1600px]:whitespace-nowrap max-[1600px]:whitespace-normal max-[1600px]:leading-tight">
                <span className="min-[1600px]:inline max-[1600px]:hidden">{t('leads.lastAction')}</span>
                <span className="hidden max-[1600px]:inline">
                  {(() => {
                    const parts = t('leads.lastAction').split(' ');
                    if (parts.length <= 1) return t('leads.lastAction');
                    return (
                      <>
                        {parts[0]}
                        <br />
                        {parts.slice(1).join(' ')}
                      </>
                    );
                  })()}
                </span>
              </TableHead>
              <TableHead className="hidden xl:table-cell text-center p-0 min-[1600px]:whitespace-nowrap max-[1600px]:whitespace-normal max-[1600px]:leading-tight">{t('leads.nextAction')}</TableHead>
              <TableActionsHeader label={t('common.actions')} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {leads.length === 0 ? (
              <TableEmptyRow colSpan={LEAD_TABLE_COLUMN_COUNT}>{emptyState}</TableEmptyRow>
            ) : (
              leads.map((lead) => (
              <ContextMenuTarget<HTMLTableRowElement> key={lead.id}>
                {(rowRef) => (
              <TableRow
                ref={rowRef}
                data-lead-id={lead.id}
                className={isSelected(lead.id) ? tableRowSelectedClass : ''}
                onClick={hideRowCheckboxes ? undefined : () => onEdit(lead)}
              >
                <TableSelectionCell
                  itemId={lead.id}
                  isSelected={isSelected(lead.id)}
                  onToggle={handleSelectItem}
                  hidden={hideRowCheckboxes}
                />
                <TableCell className="overflow-hidden">
                  <TruncateTooltip
                    text={lead.name}
                    className={`max-w-full font-medium ${neutralColors.grayTextHover}`}
                  />
                </TableCell>

                <TableCell className="hidden sm:table-cell overflow-hidden w-[32%] lg:w-[24%] xl:w-[20%]">
                  {lead.contact_type_id && lead.contact_value ? (() => {
                    const contactCategory = categories.find((c: Category) => c.id === lead.contact_type_id);
                    const channel = resolveContactChannel(contactCategory);
                    const openUrl = buildContactOpenUrl(channel, lead.contact_value);
                    const labelKey = contactOpenLabelKey(channel);
                    const openLabel = labelKey ? t(labelKey) : undefined;
                    const opensInNewTab = channel !== 'phone';

                    if (!openUrl) {
                      return (
                        <div className="inline-flex max-w-full items-center gap-2">
                          <ContactIconWithCategory
                            category={contactCategory}
                            value={lead.contact_value}
                            size="sm"
                            showTooltip={false}
                            className={neutralColors.grayTextLight}
                          />
                          <TruncateTooltip
                            text={lead.contact_value}
                            className={`text-sm ${neutralColors.grayText} min-w-0`}
                          />
                        </div>
                      );
                    }

                    const link = (
                      <a
                        href={openUrl}
                        target={opensInNewTab ? '_blank' : undefined}
                        rel={opensInNewTab ? 'noopener noreferrer' : undefined}
                        aria-label={openLabel}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="group/contact inline-flex max-w-full cursor-pointer items-center gap-2 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1"
                      >
                        <ContactIconWithCategory
                          category={contactCategory}
                          value={lead.contact_value}
                          size="sm"
                          showTooltip={false}
                          className={`${neutralColors.grayTextLight} group-hover/contact:text-primary-600 transition-colors`}
                        />
                        <span
                          className={`min-w-0 truncate text-sm transition-colors ${neutralColors.grayText} group-hover/contact:text-primary-700`}
                        >
                          {lead.contact_value}
                        </span>
                      </a>
                    );

                    if (!openLabel) return link;

                    return (
                      <Tooltip content={openLabel} wrapperClassName="inline-flex max-w-full">
                        {link}
                      </Tooltip>
                    );
                  })() : (
                    <span className={`text-sm ${neutralColors.grayTextLight}`}>-</span>
                  )}
                </TableCell>

                <TableCell className="hidden sm:table-cell text-center px-2 py-2 w-10">
                  {lead.direction && (
                    <Tooltip content={lead.direction === 'inbound' ? 'Inbound' : 'Outbound'}>
                      <div
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-lg ${
                          lead.direction === 'inbound'
                            ? 'bg-blue-50 border border-blue-200'
                            : 'bg-orange-50 border border-orange-200'
                        }`}
                      >
                        {lead.direction === 'inbound' ? (
                          <ArrowDownLeft
                            className="w-4 h-4 text-blue-600"
                            strokeWidth={2.5}
                          />
                        ) : (
                          <ArrowUpRight
                            className="w-4 h-4 text-orange-600"
                            strokeWidth={2.5}
                          />
                        )}
                      </div>
                    </Tooltip>
                  )}
                </TableCell>

                <TableCell className="hidden lg:table-cell text-center px-1 py-0">
                  <div className="mx-auto break-words leading-tight hyphens-auto min-[1600px]:whitespace-nowrap min-[1600px]:max-w-none max-[1600px]:whitespace-normal max-[1600px]:max-w-[96px]">
                    <span className={`text-[11.5px] xl:text-[12px] 2xl:text-[12.5px] ${neutralColors.grayTextHover}`}>
                      {normalizeWrap(getSectorLabel(lead.sector_id))}
                    </span>
                  </div>
                </TableCell>

                <TableCell className="text-center w-[9rem]">
                  <div className="mx-auto inline-flex justify-center">
                    <LeadStatusBadge status={lead.status} className="whitespace-nowrap" />
                  </div>
                </TableCell>

                <TableCell className="hidden lg:table-cell text-center px-0 py-0">
                  {lead.lastActionAt ? (
                    <span
                      className={`inline-block text-sm align-top min-[1600px]:whitespace-nowrap min-[1600px]:max-w-none max-[1600px]:whitespace-normal max-[1600px]:max-w-[100px] ${neutralColors.grayText}`}
                    >
                      {formatDateShort(lead.lastActionAt)}
                    </span>
                  ) : (
                    <span className={`text-sm ${neutralColors.grayTextLight}`}>-</span>
                  )}
                </TableCell>

                <TableCell className="hidden xl:table-cell text-center px-0 py-0">
                  {lead.nextActionAt ? (
                    <span
                      className={`inline-block text-sm align-top min-[1600px]:whitespace-nowrap min-[1600px]:max-w-none max-[1600px]:whitespace-normal max-[1600px]:max-w-[100px] ${
                        isFollowUpOverdue(lead)
                          ? 'text-red-600 font-medium'
                          : neutralColors.grayText
                      }`}
                    >
                      {formatDateShort(lead.nextActionAt)}
                    </span>
                  ) : (
                    <span className={`text-sm ${neutralColors.grayTextLight}`}>-</span>
                  )}
                </TableCell>

                <TableActionsCell>
                  <ContextMenu
                    contextTargetRef={rowRef}
                    items={getLeadActionMenuItems({
                      lead,
                      projects,
                      contactCategory: categories.find((c: Category) => c.id === lead.contact_type_id),
                      t,
                      context: 'table',
                      preferCreateProject: guidedMenuLeadId === lead.id,
                      onContact: leadMenuActions?.onContact,
                      onMessage: leadMenuActions?.onMessage,
                      onReschedule: leadMenuActions?.onReschedule,
                      onCreateProject: leadMenuActions?.onCreateProject,
                      onViewProject: leadMenuActions?.onViewProject,
                      onMarkAsAbandoned: leadMenuActions?.onMarkAsAbandoned,
                      onMarkAsRejected: leadMenuActions?.onMarkAsRejected,
                      onMoveToNextStatus: leadMenuActions?.onMoveToNextStatus,
                      onEdit,
                      onDelete: (l) => onDelete(l.id),
                      omitEditDeleteFromMenu: true,
                    })}
                    trigger="more"
                    size="sm"
                    tooltip={t('common.moreActions')}
                    forceOpen={guidedMenuLeadId === lead.id}
                    onOpenChange={(isOpen) => {
                      if (!isOpen && guidedMenuLeadId === lead.id) {
                        onGuidedMenuClose?.();
                      }
                    }}
                    triggerClassName={
                      guidedMenuLeadId === lead.id
                        ? `${highlightRing.ring} ring-offset-2 bg-blue-50`
                        : ''
                    }
                  />
                  <ActionIconButton
                    icon={Edit}
                    onClick={() => onEdit(lead)}
                    variant="edit"
                    size="sm"
                    tooltip={t('common.edit')}
                  />
                  <ActionIconButton
                    icon={Trash2}
                    onClick={() => onDelete(lead.id)}
                    variant="delete"
                    size="sm"
                    tooltip={t('common.delete')}
                  />
                </TableActionsCell>
              </TableRow>
                )}
              </ContextMenuTarget>
              ))
            )}
          </TableBody>
        </Table>
        
        {/* Infinite load more */}
        {onLoadMore && (
          <InfiniteLoadMore
            hasMore={hasMore}
            loading={loadingMore}
            hasItems={leads.length > 0}
            onLoadMore={onLoadMore}
          />
        )}
      </div>
    </div>
  );
};

export default LeadTable;
